/**
 * R10e36.71.1-F778: Desktop-Only Route Wrapper
 * 
 * Restricts complex admin pages from mobile viewports (<768px).
 * Displays a user-friendly "Desktop Required" message on mobile devices.
 */
import React from 'react';
import { Monitor, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

// R10e36.71.1-F778: Export hook for use in mobile menu filtering
export const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on mount
    checkMobile();

    // Add resize listener
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

// R10e36.71.1-F778: Routes that require desktop viewport
// R10e36.71.2.6-F778-R2: Added trust-account, payment-review, reports for mobile
export const MOBILE_HIDDEN_ROUTES = [
  '/operations',
  '/collector/bill-runs',
  '/collector/billing/templates',
  '/collector/boa-reconciliation',
  '/audit-logs',
  '/historical-import',
  '/collector/settings',
  '/certificate-admin',
  '/collector/millage-rates',
  '/collector/millage-adjustment',
  '/collector/assessment-changes',
  '/collector/charges',
  '/collector/mail-queue',
  '/migrations',
  '/collector/first-time-setup',
  // R10e36.71.2.6-F778-R2: Complex admin workflows hidden on mobile
  '/trust-account',
  '/payment-review',
  '/reports'
];

export const DesktopOnly = ({ children, pageName = 'This page' }) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  if (isMobile) {
    return (
      <div 
        className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4"
        data-testid="desktop-only-message"
      >
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-center">
          <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Monitor className="w-8 h-8 text-blue-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-slate-900 mb-3">
            Desktop Required
          </h1>
          
          <p className="text-slate-600 mb-6 leading-relaxed">
            {pageName} requires a larger screen for the best experience. 
            Please access this page from a desktop or laptop computer.
          </p>
          
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/dashboard')}
              className="w-full"
              data-testid="desktop-only-go-dashboard"
            >
              Go to Dashboard
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => navigate(-1)}
              className="w-full"
              data-testid="desktop-only-go-back"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </div>
          
          <p className="text-xs text-slate-400 mt-6">
            Minimum screen width: 768px
          </p>
        </div>
      </div>
    );
  }

  return children;
};

export default DesktopOnly;
